"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ClassesBy,
  computeTable,
  type DataType,
  promptFor,
  type Settings,
  sturgesHint,
  type TableModel,
  type TableType,
} from "@/components/tools/stats-descriptive/frequency-views";
import {
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type CategoryOrder,
  type GroupedTable,
  parseCategories,
  sturgesClassCount,
  toCsv,
  toMarkdownTable,
} from "@/lib/tool-kit/stats-descriptive/frequency";

/** English strings. A translated content file overrides these through `ui`. */
export const FREQUENCY_DISTRIBUTION_CALCULATOR_STRINGS = {
  dataTypeLabel: "Type of data",
  dataNumbers: "Numbers",
  dataCategories: "Categories (words or labels)",
  numbersLabel: "Your data — numbers separated by commas, spaces, or new lines",
  numbersPlaceholder: "41, 35, 20, 44, 38, 29, 46",
  categoriesLabel: "Your data — one label per line, or separated by commas",
  categoriesPlaceholder: "Bus\nWalk\nCar\nBus",
  valuesDetected: "one: value detected|other: values detected",
  tableLabel: "Table",
  tableGrouped: "Grouped (class intervals)",
  tableUngrouped: "Ungrouped (one row per value)",
  classesByLabel: "Set the classes by",
  byCount: "Number of classes",
  byWidth: "Class width",
  classCountLabel: "Number of classes (k)",
  classCountHint: "Leave blank for Sturges' rule: {classes} for {values}.",
  classCountHintClasses: "one:{count} class|other:{count} classes",
  classCountHintValues: "one:{count} value|other:{count} values",
  widthLabel: "Class width",
  widthPlaceholder: "5",
  startLabel: "First class starts at (optional)",
  startHint: "Leave blank to start at the minimum, {min}.",
  orderLabel: "Order",
  orderFirst: "As they first appear",
  orderAlphabetical: "Alphabetical",
  orderFrequency: "Most frequent first",
  ignoreCase: "Treat upper and lower case as the same",
  tryExample: "Try an example",
  exampleNumbers:
    "41, 35, 20, 44, 38, 29, 46, 33, 40, 37, 42, 24, 48, 36, 31, 44, 39, 27, 45, 41, 34, 49, 38, 32, 43, 35, 40, 47, 37, 42",
  exampleCategories:
    "Bus, Walk, Car, Bus, Bike, Walk, Bus, Train, Car, Walk, Bus, Bike, Car, Walk, Bus, Train, Bike, Car, Walk, Bus",
  clear: "Clear",
  enterPrompt: "Enter at least two values to build a frequency table.",
  results: "Frequency distribution",
  colClass: "Class",
  colBoundaries: "Class boundaries",
  colMidpoint: "Midpoint",
  colValue: "Value",
  colCategory: "Category",
  colFrequency: "Frequency",
  colRelative: "Relative frequency (%)",
  colCumulative: "Cumulative frequency",
  colCumulativeRelative: "Cumulative relative frequency (%)",
  total: "Total",
  rangeTemplate: "{from}–{to}",
  copyCsv: "Copy as CSV",
  copyMarkdown: "Copy as Markdown",
  copied: "Copied!",
  workingTitle: "How the classes were built",
  sturgesLine:
    "Sturges' rule: k = ⌈1 + log₂ n⌉ = ⌈1 + log₂ {n}⌉ = ⌈{raw}⌉ = {k} classes",
  chosenCount: "Number of classes: k = {k}, as you set it",
  rangeLine: "Range = maximum − minimum = {max} − {min} = {range}",
  widthRounded:
    "Class width = range ÷ k = {range} ÷ {k} ≈ {raw}, rounded up to the data's precision ({unit}) → {width}",
  widthExact:
    "Class width = range ÷ k = {range} ÷ {k} = {raw} exactly, so add one unit ({unit}) to leave room for the maximum → {width}",
  widthGiven: "Class width = {width}, as you set it",
  unitLine:
    "The data are recorded to the nearest {unit}, so each class boundary sits {half} outside its class limit.",
  classesLine:
    "one: {count} class of width {width}, starting at {start}, covers every value from {min} to {max}.|other: {count} classes of width {width}, starting at {start}, cover every value from {min} to {max}.",
  tooManyRows:
    "one:Your data have {count} distinct value — too many rows for a useful table. Switch to a grouped table.|other:Your data have {count} distinct values — too many rows for a useful table. Switch to a grouped table.",
  errorStart: "The first class must start at or below the minimum, {min}.",
  errorTooMany:
    "one:That setting makes {count} class. Use a wider class or fewer classes (at most {max}).|other:That setting makes {count} classes. Use a wider class or fewer classes (at most {max}).",
  errorWidth: "Enter a class width greater than zero.",
  enterWidth: "Enter a class width to build the table.",
  errorCount: "Enter a whole number of classes, 1 or more.",
} as const;

type Strings = typeof FREQUENCY_DISTRIBUTION_CALCULATOR_STRINGS;

function GroupedWorking({
  table,
  strings,
  fmt,
  locale,
}: {
  table: GroupedTable;
  strings: Strings;
  fmt: NumberFormatter;
  locale: string;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const k = table.requestedClassCount;
  const values = {
    n: fmt(table.n),
    k: k === null ? "" : fmt(k),
    raw: num(1 + Math.log2(table.n)),
    min: num(table.min),
    max: num(table.max),
    range: num(table.range),
    unit: num(table.unit),
    half: num(table.unit / 2),
    width: num(table.width),
    start: num(table.start),
  };
  let widthLine = t(strings.widthGiven, values);
  if (k !== null) {
    const raw = num(table.rawWidth as number);
    widthLine = table.bumpedExactWidth
      ? t(strings.widthExact, { ...values, raw })
      : t(strings.widthRounded, { ...values, raw });
  }
  return (
    <WorkingPanel title={strings.workingTitle}>
      {table.usedSturges ? (
        <Formula>{t(strings.sturgesLine, values)}</Formula>
      ) : null}
      {k !== null && !table.usedSturges ? (
        <Formula>{t(strings.chosenCount, values)}</Formula>
      ) : null}
      <Formula>{t(strings.rangeLine, values)}</Formula>
      <Formula>{widthLine}</Formula>
      <Formula>{t(strings.unitLine, values)}</Formula>
      <Formula>
        {tPlural(strings.classesLine, locale, table.rows.length, {
          ...values,
          count: fmt(table.rows.length),
        })}
      </Formula>
    </WorkingPanel>
  );
}

function TableOutput({
  model,
  strings,
}: {
  model: TableModel;
  strings: Strings;
}) {
  return (
    <>
      <ResultTable
        footer={model.totalDisplay}
        head={model.head}
        rows={model.display}
      />
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyCsv}
          text={toCsv(model.head, model.machine)}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyMarkdown}
          text={toMarkdownTable(model.head, [
            ...model.machine,
            model.totalMachine,
          ])}
        />
      </div>
    </>
  );
}

function ClassSettings({
  settings,
  setClassesBy,
  setCountText,
  setWidthText,
  setStartText,
  strings,
  fmt,
  locale,
}: {
  settings: Settings;
  setClassesBy: (value: ClassesBy) => void;
  setCountText: (value: string) => void;
  setWidthText: (value: string) => void;
  setStartText: (value: string) => void;
  strings: Strings;
  fmt: NumberFormatter;
  locale: string;
}) {
  const { values } = settings;
  const sturgesCount = sturgesClassCount(Math.max(1, values.length));
  const sturges = fmt(sturgesCount);
  const minimum =
    values.length > 0 ? values.reduce((a, b) => Math.min(a, b)) : null;
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.classesByLabel}
        name="fd-classes-by"
        onChange={setClassesBy}
        options={[
          { value: "count", label: strings.byCount },
          { value: "width", label: strings.byWidth },
        ]}
        value={settings.classesBy}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {settings.classesBy === "count" ? (
          <TextField
            hint={sturgesHint(
              strings,
              locale,
              fmt,
              sturgesCount,
              values.length
            )}
            id="fd-count"
            label={strings.classCountLabel}
            numeric
            onChange={setCountText}
            placeholder={sturges}
            value={settings.countText}
          />
        ) : (
          <TextField
            id="fd-width"
            label={strings.widthLabel}
            numeric
            onChange={setWidthText}
            placeholder={strings.widthPlaceholder}
            value={settings.widthText}
          />
        )}
        <TextField
          hint={
            minimum === null
              ? undefined
              : t(strings.startHint, { min: formatStat(fmt, minimum) })
          }
          id="fd-start"
          label={strings.startLabel}
          numeric
          onChange={setStartText}
          value={settings.startText}
        />
      </div>
    </div>
  );
}

export default function FrequencyDistributionCalculatorWidget() {
  const strings = useToolUi(FREQUENCY_DISTRIBUTION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [dataType, setDataType] = useState<DataType>("numbers");
  const [numbersText, setNumbersText] = useState("");
  const [categoriesText, setCategoriesText] = useState("");
  const [tableType, setTableType] = useState<TableType>("grouped");
  const [classesBy, setClassesBy] = useState<ClassesBy>("count");
  const [countText, setCountText] = useState("");
  const [widthText, setWidthText] = useState("");
  const [startText, setStartText] = useState("");
  const [order, setOrder] = useState<CategoryOrder>("first");
  const [ignoreCase, setIgnoreCase] = useState(true);

  const settings: Settings = {
    dataType,
    values: parseNumberList(numbersText),
    items: parseCategories(categoriesText),
    tableType,
    classesBy,
    countText,
    widthText,
    startText,
    order,
    ignoreCase,
  };
  const isNumbers = dataType === "numbers";
  const n = isNumbers ? settings.values.length : settings.items.length;
  const { model, grouped, problem } = computeTable(
    settings,
    strings,
    fmt,
    locale
  );
  const input = isNumbers
    ? {
        label: strings.numbersLabel,
        placeholder: strings.numbersPlaceholder,
        value: numbersText,
        onChange: setNumbersText,
        example: strings.exampleNumbers,
      }
    : {
        label: strings.categoriesLabel,
        placeholder: strings.categoriesPlaceholder,
        value: categoriesText,
        onChange: setCategoriesText,
        example: strings.exampleCategories,
      };

  function clear() {
    setNumbersText("");
    setCategoriesText("");
    setCountText("");
    setWidthText("");
    setStartText("");
  }

  return (
    <>
      <section className="space-y-4">
        <PillGroup
          label={strings.dataTypeLabel}
          name="fd-type"
          onChange={setDataType}
          options={[
            { value: "numbers", label: strings.dataNumbers },
            { value: "categories", label: strings.dataCategories },
          ]}
          value={dataType}
        />
        <TextAreaField
          hint={
            <>
              {fmt(n)}
              {tPlural(strings.valuesDetected, locale, n)}
            </>
          }
          id="fd-data"
          label={input.label}
          onChange={input.onChange}
          placeholder={input.placeholder}
          value={input.value}
        />
        {isNumbers ? (
          <PillGroup
            label={strings.tableLabel}
            name="fd-table"
            onChange={setTableType}
            options={[
              { value: "grouped", label: strings.tableGrouped },
              { value: "ungrouped", label: strings.tableUngrouped },
            ]}
            value={tableType}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="fd-order"
              label={strings.orderLabel}
              onChange={setOrder}
              options={[
                { value: "first", label: strings.orderFirst },
                { value: "alphabetical", label: strings.orderAlphabetical },
                { value: "frequency", label: strings.orderFrequency },
              ]}
              value={order}
            />
            <div className="flex items-end pb-2">
              <Checkbox
                checked={ignoreCase}
                label={strings.ignoreCase}
                onChange={setIgnoreCase}
              />
            </div>
          </div>
        )}
        {isNumbers && tableType === "grouped" ? (
          <ClassSettings
            fmt={fmt}
            locale={locale}
            setClassesBy={setClassesBy}
            setCountText={setCountText}
            setStartText={setStartText}
            settings={settings}
            setWidthText={setWidthText}
            strings={strings}
          />
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => input.onChange(input.example)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
      </section>

      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {model ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <TableOutput model={model} strings={strings} />
          {grouped ? (
            <GroupedWorking
              fmt={fmt}
              locale={locale}
              strings={strings}
              table={grouped}
            />
          ) : null}
        </section>
      ) : null}
      {model || problem ? null : (
        <Notice>{promptFor(settings, n, strings)}</Notice>
      )}
    </>
  );
}
